import type { Enrollment, MembershipItem } from '@spike/membership-model';
import {
    computeMembershipDisplayPrice,
    computeMembershipPreviewDiscount,
    getMembershipRemainingUses,
    isEnrollmentAvailableOnDate,
    resolveMembershipBasePrice,
    shouldApplyMembershipToAllAppointments
} from 'components/CreateBooking/utils/membershipDisplayPrice';
import { Training, DataVaraible, PetServiceWrapper } from 'model/NewMultipleBooking';
import { Moment } from 'moment-timezone';
import { getTrainingDateSlotsForSave } from 'utils/trainingDateSlots';

const enrollmentFromTraining = (training: Training): Enrollment | undefined => training.enrollment;

const membershipItemFromTraining = (training: Training): MembershipItem | undefined => training.membershipItem;

export const buildTrainingVariableDataPerSlot = (
    training: Training,
    fallbackDate: Moment,
    timezone: string,
    isEditMode = false,
    membershipUsesAlreadyAllocated = 0,
    catalogPriceOverride?: number
): Array<DataVaraible> => {
    const durationInMinutes =
        training.service?.duration ||
        (training.from && training.to ? training.to.diff(training.from, 'minutes') : 0) ||
        0;
    const trainingEnrollment = enrollmentFromTraining(training);
    const membershipItem = membershipItemFromTraining(training);
    const catalogPrice = catalogPriceOverride ?? training.service?.price ?? training.price ?? 0;
    const slotLinePrice = training.price || training.service?.price || catalogPrice || 0;

    const baseTrainingService: PetServiceWrapper = {
        service: {
            id: training.service?.serviceId || 0,
            name: training.service?.serviceName || 'Training Service'
        },
        price: slotLinePrice,
        beforePrice: catalogPrice > 0 ? catalogPrice : training.service?.price ?? training.price,
        durationInMinutes: durationInMinutes || 0,
        duration: {
            hours: durationInMinutes ? Math.trunc(durationInMinutes / 60) : 0,
            minutes: durationInMinutes ? durationInMinutes % 60 : 0
        },
        discount: {
            type: training.discount?.type || 'amount',
            value: training.discount?.value ?? 0
        },
        businessArea: 'training',
        color: training.service?.color ?? '',
        bookingType: training.service?.bookingType,
        maxPets: training.service?.maxPets,
        address: training.service?.inHomeService ? training.address : '',
        instructions: training.service?.inHomeService ? training.instructions : undefined,
        inHomeService: training.service?.inHomeService ?? false,
        saveCustomPrice: training.saveCustomPrice,
        customPriceId: training.customPriceId,
        customPriceAmount: slotLinePrice > 0 ? slotLinePrice : undefined
    };

    const trainingSlots = getTrainingDateSlotsForSave(training, fallbackDate, timezone, isEditMode);
    const applyMembershipToAll =
        trainingEnrollment != null &&
        membershipItem != null &&
        shouldApplyMembershipToAllAppointments(membershipItem, trainingEnrollment);
    let limitedMembershipUsesRemaining =
        trainingEnrollment != null && membershipItem != null && !applyMembershipToAll
            ? Math.max(
                  0,
                  getMembershipRemainingUses(membershipItem, trainingEnrollment) - membershipUsesAlreadyAllocated
              )
            : Number.POSITIVE_INFINITY;

    return trainingSlots.map(slot => {
        const membershipAvailableOnDate =
            trainingEnrollment != null &&
            membershipItem != null &&
            isEnrollmentAvailableOnDate(trainingEnrollment, slot.date, timezone);
        let applyMembership = false;

        if (membershipAvailableOnDate) {
            if (applyMembershipToAll) {
                applyMembership = true;
            } else if (limitedMembershipUsesRemaining > 0) {
                applyMembership = true;
                limitedMembershipUsesRemaining -= 1;
            }
        }

        const serviceForSlot: PetServiceWrapper = applyMembership
            ? (() => {
                  const membershipBase = resolveMembershipBasePrice(catalogPrice, slotLinePrice);
                  const membershipPricing = computeMembershipDisplayPrice(membershipBase, membershipItem);
                  const previewDiscount = computeMembershipPreviewDiscount(membershipBase, membershipItem);
                  return {
                      ...baseTrainingService,
                      price: membershipPricing.displayPrice,
                      beforePrice: membershipBase,
                      customPriceAmount: membershipBase > 0 ? membershipBase : undefined,
                      discount: previewDiscount
                          ? { type: previewDiscount.type, value: previewDiscount.value }
                          : { type: 'amount' as const, value: 0 },
                      enrollment: trainingEnrollment,
                      membershipItem
                  };
              })()
            : {
                  ...baseTrainingService,
                  price: slotLinePrice,
                  beforePrice: catalogPrice > 0 ? catalogPrice : baseTrainingService.beforePrice,
                  discount: { type: 'amount', value: 0 },
                  enrollment: undefined,
                  membershipItem: undefined
              };

        const discountForSlot =
            trainingEnrollment != null && membershipItem != null && !applyMembership
                ? { type: 'amount' as const, value: 0 }
                : applyMembership
                ? (() => {
                      const membershipBase = resolveMembershipBasePrice(catalogPrice, slotLinePrice);
                      const previewDiscount = computeMembershipPreviewDiscount(membershipBase, membershipItem);
                      return previewDiscount
                          ? {
                                type: previewDiscount.type as 'amount' | 'percentage',
                                value: previewDiscount.value
                            }
                          : { type: 'percentage' as const, value: 100 };
                  })()
                : {
                      type: (training.discount?.type || 'amount') as 'amount' | 'percentage',
                      value: training.discount?.value ?? 0
                  };

        const sessionIdForSlot =
            slot.serviceSessionId !== undefined
                ? slot.serviceSessionId
                : training.serviceSessionId != null &&
                  training.from?.isValid() &&
                  slot.date.clone().tz(timezone).format('YYYY-MM-DD') ===
                      training.from.clone().tz(timezone).format('YYYY-MM-DD')
                ? training.serviceSessionId
                : null;

        return {
            service: { ...serviceForSlot, serviceSessionId: sessionIdForSlot },
            staffId: slot.staffId ?? training.staffId,
            date: slot.date,
            dateEnd: slot.dateEnd,
            itemData: {
                discount: discountForSlot,
                flow: 'training',
                ...(catalogPrice > 0 || slotLinePrice > 0
                    ? { price: resolveMembershipBasePrice(catalogPrice, slotLinePrice) }
                    : {})
            }
        };
    });
};
